import React from 'react';
import { 
  Mail, 
  Phone, 
  Instagram, 
  Linkedin, 
  MessageSquare,
  Heart,
  ArrowUp
} from 'lucide-react';
import { personalInfo } from '../data/mock';

const Footer = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const currentYear = new Date().getFullYear();

  const socialLinks = [
    { 
      icon: Instagram, 
      href: `https://instagram.com/${personalInfo.contact.socials.instagram.replace('@', '')}`, 
      label: 'Instagram' 
    },
    { 
      icon: Linkedin, 
      href: `https://linkedin.com/in/${personalInfo.contact.socials.linkedin}`, 
      label: 'LinkedIn' 
    }
  ];

  const footerLinks = [
    {
      title: 'Services',
      links: [
        { name: 'Personal Brand Strategy', href: '#services' },
        { name: 'Social Media Strategy', href: '#services' },
        { name: 'UGC & Content Creation', href: '#services' },
        { name: 'Video Editing', href: '#services' }
      ]
    },
    {
      title: 'Company',
      links: [
        { name: 'About', href: '#about' },
        { name: 'Portfolio', href: '#portfolio' },
        { name: 'Testimonials', href: '#testimonials' },
        { name: 'Contact', href: '#contact' }
      ]
    },
    {
      title: 'Resources',
      links: [
        { name: 'Brand Strategy Guide', href: '#' },
        { name: 'Content Templates', href: '#' },
        { name: 'Growth Calculator', href: '#' },
        { name: 'Free Consultation', href: '#contact' }
      ]
    }
  ];

  return (
    <footer className="bg-gray-900 text-white">
      {/* Main Footer Content */}
      <div className="max-w-6xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
          {/* Brand Section */}
          <div className="lg:col-span-2">
            <div className="mb-6">
              <h3 className="text-2xl font-bold mb-2">{personalInfo.brand}</h3>
              <p className="text-gray-400 leading-relaxed">
                Elevating brands through strategic content creation and authentic storytelling. Let's turn your vision into measurable success.
              </p>
            </div>
            
            {/* Contact Info */}
            <div className="space-y-3 mb-6">
              <div className="flex items-center gap-3">
                <Mail className="h-4 w-4 text-pink-400" />
                <a 
                  href={`mailto:${personalInfo.contact.email}`}
                  className="text-gray-400 hover:text-white transition-colors duration-200"
                >
                  {personalInfo.contact.email}
                </a>
              </div>
              
              <div className="flex items-center gap-3">
                <Phone className="h-4 w-4 text-pink-400" />
                <a 
                  href={`tel:${personalInfo.contact.phone}`}
                  className="text-gray-400 hover:text-white transition-colors duration-200"
                >
                  {personalInfo.contact.phone}
                </a>
              </div>
            </div>

            {/* Social Links */}
            <div className="flex gap-4">
              {socialLinks.map((social, index) => {
                const IconComponent = social.icon;
                return (
                  <a
                    key={index}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-gray-800 hover:bg-pink-600 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110"
                    aria-label={social.label}
                  >
                    <IconComponent className="h-4 w-4" />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Footer Links */}
          {footerLinks.map((section, index) => (
            <div key={index}>
              <h4 className="text-lg font-semibold mb-4 text-white">
                {section.title}
              </h4>
              <ul className="space-y-2">
                {section.links.map((link, linkIndex) => (
                  <li key={linkIndex}>
                    <a
                      href={link.href}
                      className="text-gray-400 hover:text-white transition-colors duration-200 text-sm"
                    >
                      {link.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Newsletter Signup */}
        <div 
          className="mt-12 p-8 rounded-2xl"
          style={{
            background: `linear-gradient(135deg, rgba(255, 230, 254, 0.1) 0%, rgba(253, 236, 255, 0.1) 50%, rgba(222, 228, 208, 0.1) 100%)`
          }}
        >
          <div className="text-center">
            <h4 className="text-xl font-semibold mb-2">Stay Updated</h4>
            <p className="text-gray-400 mb-6">
              Get the latest tips, trends, and strategies delivered to your inbox
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-4 py-3 bg-gray-800 border border-gray-700 rounded-full text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-500"
              />
              <button className="bg-pink-600 hover:bg-pink-700 text-white px-6 py-3 rounded-full font-medium transition-all duration-300 hover:scale-105">
                Subscribe
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="max-w-6xl mx-auto px-6 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-1 text-gray-400 text-sm">
              <span>© {currentYear} {personalInfo.brand}. Made with</span>
              <Heart className="h-4 w-4 text-pink-400 fill-current" />
              <span>for ambitious brands</span>
            </div>
            
            <div className="flex items-center gap-6">
              <a href="#" className="text-gray-400 hover:text-white text-sm transition-colors duration-200">
                Privacy Policy
              </a>
              <a href="#" className="text-gray-400 hover:text-white text-sm transition-colors duration-200">
                Terms of Service
              </a>
              <button
                onClick={scrollToTop}
                className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors duration-200 text-sm group"
              >
                Back to top
                <ArrowUp className="h-4 w-4 group-hover:-translate-y-1 transition-transform duration-200" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;